export interface IPinInputOptions {
  availableCharsRE?: RegExp;
}

export interface IPinInput {
  options?: IPinInputOptions;
}

export interface ICollectionItem<T> {
  id: string | number;
  element: T;
}

const dispatch = (evt: string, element: any, payload: any = null) => {
  const event = new CustomEvent(evt, {
    detail: { payload },
    bubbles: true,
    cancelable: true,
    composed: false,
  });

  element.dispatchEvent(event);
};

class HSPinInput implements IPinInput {
  private el: HTMLElement;
  private events?: any;
  private items: NodeListOf<HTMLElement>;
  private currentItem: HTMLInputElement | null;
  private currentValue: string[] = [];
  private readonly placeholders: string[] = [];
  private readonly availableCharsRE: RegExp | null;
  options?: IPinInputOptions;

  constructor(el: HTMLElement, options?: IPinInputOptions) {
    this.el = el;
    this.options = options;
    this.events = {};

    const data = el.getAttribute('data-hs-pin-input');
    const dataOptions: IPinInputOptions = data ? JSON.parse(data) : {};
    const concatOptions = {
      ...dataOptions,
      ...options,
    };

    this.items = this.el.querySelectorAll('[data-hs-pin-input-item]');
    this.currentItem = null;
    this.currentValue = new Array(this.items.length).fill('');
    this.placeholders = [];
    this.availableCharsRE = new RegExp(concatOptions?.availableCharsRE || '^[a-zA-Z0-9]+$'); // '^[0-9]+$'

    this.init();
  }

  public createCollection(collection: any[], element: any) {
    collection.push({
      id: element?.el?.id || collection.length + 1,
      element,
    });
  }

  public fireEvent(evt: string, payload: any = null) {
    if (this.events.hasOwnProperty(evt)) return this.events[evt](payload);
  }

  public on(evt: string, cb: Function) {
    this.events[evt] = cb;
  }

  private init() {
    this.createCollection(window.$hsPinInputCollection, this);

    if (this.items?.length) this.build();
  }

  private build() {
    this.buildInputItems();
  }

  private buildInputItems() {
    this.items?.forEach((el, index) => {
      this.placeholders?.push(el.getAttribute('placeholder') || '');

      el.addEventListener('input', (evt) => this.onInput(evt, index));
      el.addEventListener('paste', (evt) => this.onPaste(evt));
      el.addEventListener('keydown', (evt) => this.onKeydown(evt, index));
      el.addEventListener('focusout', () => this.onFocusOut(index));
    });
  }

  private checkIfNumber(value: string) {
    if (this.availableCharsRE === null) return true;
    return value.match(this.availableCharsRE);
  }

  private autoFillAll(text: string) {
    Array.from(text).forEach((n, i) => {
      if (!(this.items && this.items[i])) return false;

      (this.items[i] as HTMLInputElement).value = n;

      this.items[i].dispatchEvent(new Event('input', { bubbles: true }));
    });
  }

  private setCurrentValue() {
    this.currentValue = Array.from(this.items || []).map((el) => (el as HTMLInputElement).value);
  }

  private toggleCompleted() {
    if (!this.currentValue?.includes('')) this.el.classList.add('active');
    else this.el.classList.remove('active');
  }

  private onInput(evt: Event, index: number) {
    const originalValue = (evt.target as HTMLInputElement).value;
    this.currentItem = evt.target as HTMLInputElement;
    this.currentItem.value = '';
    this.currentItem.value = originalValue[originalValue.length - 1];

    if (!this.checkIfNumber(this.currentItem.value)) {
      this.currentItem.value = (this.currentValue && this.currentValue[index]) || '';

      return false;
    }

    this.setCurrentValue();

    if (this.currentItem.value) {
      if (index < this.items.length - 1) this.items[index + 1].focus();
      if (!this.currentValue.includes('')) {
        const payload = { currentValue: this.currentValue };

        this.fireEvent('completed', payload);
        dispatch('completed.hs.pinInput', this.el, payload);
      }

      this.toggleCompleted();
    } else {
      if (index > 0) this.items[index - 1].focus();
    }
  }

  private onKeydown(evt: KeyboardEvent, index: number) {
    if (evt.key === 'ArrowLeft' && index > 0) {
      (this.items[index - 1] as HTMLInputElement).focus();
    } else if (evt.key === 'ArrowRight' && index < this.items.length - 1) {
      (this.items[index + 1] as HTMLInputElement).focus();
    }

    if (evt.key === 'Backspace' && index > 0) {
      if ((this.items[index] as HTMLInputElement).value === '') {
        (this.items[index - 1] as HTMLInputElement).value = '';
        (this.items[index - 1] as HTMLInputElement).focus();
      } else {
        (this.items[index] as HTMLInputElement).value = '';
      }
    }

    this.setCurrentValue();
    this.toggleCompleted();
  }

  private onFocusOut(index: number) {
    this.items[index].setAttribute('placeholder', this.placeholders[index]);
  }

  private onPaste(evt: ClipboardEvent) {
    evt.preventDefault();

    this.items.forEach((el) => {
      if (document.activeElement === el && evt.clipboardData)
        this.autoFillAll(evt.clipboardData.getData('text'));
    });
  }

  static autoInit() {
    if (!window.$hsPinInputCollection) window.$hsPinInputCollection = [];

    document
      .querySelectorAll<HTMLElement>('[data-hs-pin-input]:not(.--prevent-on-load-init)')
      .forEach((el) => {
        if (!window.$hsPinInputCollection.find((elC) => (elC?.element?.el as HTMLElement) === el))
          new HSPinInput(el);
      });
  }
}

declare global {
  interface Window {
    HSPinInput: Function;
    $hsPinInputCollection: ICollectionItem<HSPinInput>[];
  }
}

export default HSPinInput;
